/*!
 * ============================================================================
 * Solmaroc — Per-color product gallery filter
 * ============================================================================
 *
 * WHAT IT DOES
 * ------------
 * On product pages, when the customer picks a color variant, the product
 * gallery is filtered to show ONLY that color's images (front, back, side…).
 * Swiping / scrolling the gallery no longer jumps to other colors.
 *
 * WHY IT'S NEEDED
 * ---------------
 * Shopify's API only allows ONE image per variant, so the theme can't natively
 * filter the gallery by color. This script does it client-side by reading the
 * color from each image's CDN filename.
 *
 * HOW COLOR DETECTION WORKS
 * -------------------------
 * Printful mockup filenames embed the color slug:
 *   unisex-pullover-hoodie-black-heather-front-6ac35eab4622e.jpg
 *   └──┬──┘ └───┬───┘ └┬─┘ └────┬────┘
 *    blank    color    view    hash
 * The script collects every color value from the variant picker
 * ("Black", "Black Heather", …), slugifies them ("black-heather"),
 * and matches `-{color-slug}-{view}-` inside each image URL.
 * Slugs are matched longest-first so "black" never steals "black-heather".
 *
 * INSTALLATION (manual — theme code cannot be edited via API)
 * -----------------------------------------------------------
 * 1. Shopify Admin → Online Store → Themes
 * 2. On the live theme ("Ritual + Top shipping bar") click ⋯ → Edit code
 * 3. Under "Assets", click "Add a new asset" → "Create a blank file"
 *    → name it exactly:  variant-gallery-filter.js
 *    → paste this ENTIRE file into it → Save
 * 4. Open "Layout" → theme.liquid
 *    Find the closing </body> tag and paste this line right ABOVE it:
 *
 *        <script src="{{ 'variant-gallery-filter.js' | asset_url }}" defer></script>
 *
 * 5. Save. Open any product page, pick a color → the gallery now shows
 *    only that color's images. Pick another color → it switches.
 *
 * NOTES / LIMITATIONS
 * -------------------
 * - Desktop uses the grid gallery (ul.media-gallery__grid); mobile uses the
 *   slideshow carousel. Both are filtered.
 * - The slideshow web component natively skips slides with the `hidden`
 *   attribute, so carousel navigation keeps working after filtering.
 * - Thumbnails, mobile dots, the slide counter and the zoom dialog are all
 *   kept in sync (index-aligned with the slides).
 * - If a product's images carry no color info in their filenames
 *   (e.g. the joggers' `{hash}_preview.png` files), the script shows ALL
 *   images — it never hides anything it can't classify.
 * - With the "large first image" grid setting, the 2-column span stays on
 *   the (possibly hidden) first DOM item; the visible images simply flow
 *   in a normal grid. Harmless.
 *
 * Tested against: Ritual theme (Dawn-based), block `blocks/_product-media-gallery.liquid`
 * with `media_presentation: grid`, custom elements `media-gallery`,
 * `slideshow-component`, `variant-picker`, events `variant:selected` / `variant:update`.
 * ============================================================================
 */

(function () {
  'use strict';

  // Guard against double-inclusion.
  if (window.__solmarocGalleryFilter) return;
  window.__solmarocGalleryFilter = true;

  /* ============================== Config ============================== */

  var DEBUG = false; // set true to log filter activity in the console

  // Option names treated as the "color" option (slugified before comparing).
  var COLOR_OPTION_NAMES = ['color', 'colour', 'couleur', 'farbe', 'colore', 'cor'];

  // View words used in Printful/Shopify image filenames, longest first.
  // Pattern: {anything}-{color-slug}-{view}-{hexhash}.jpg
  var VIEW_WORDS = [
    'left-front',
    'right-front',
    'front',
    'back',
    'left',
    'right',
    'flat',
    'detail',
    'lifestyle',
    'ghost',
  ];

  /* ============================== Helpers ============================= */

  function log() {
    if (DEBUG && window.console && console.log) {
      console.log.apply(console, ['[gallery-filter]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function slugify(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Extract the color slug from a Shopify CDN image URL.
   * knownSlugs must be sorted longest-first (done by optionValues()).
   * Returns the matched slug, or null when no color can be determined.
   */
  function colorSlugFromUrl(url, knownSlugs) {
    if (!url || !knownSlugs || !knownSlugs.length) return null;
    var file = String(url).split('?')[0].split('/').pop().toLowerCase();
    file = file.replace(/\.[a-z0-9]+$/i, ''); // strip extension
    file = file.replace(/-[0-9a-f]{8,}$/i, ''); // strip Printful hex-hash suffix
    for (var i = 0; i < knownSlugs.length; i++) {
      var slug = knownSlugs[i];
      for (var v = 0; v < VIEW_WORDS.length; v++) {
        if (file.indexOf('-' + slug + '-' + VIEW_WORDS[v]) !== -1) return slug;
      }
    }
    return null;
  }

  /* ====================== Product page elements ======================= */

  function mainGallery() {
    return document.querySelector('media-gallery');
  }

  function mainPicker() {
    return (
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector('main variant-picker') ||
      document.querySelector('variant-picker')
    );
  }

  // The option container (fieldset for buttons/swatches, div for dropdowns)
  // that holds the color option.
  function colorOptionContainer(picker) {
    if (!picker) return null;
    var containers = picker.querySelectorAll('.variant-option');
    for (var i = 0; i < containers.length; i++) {
      var name = optionName(containers[i]);
      if (name && COLOR_OPTION_NAMES.indexOf(slugify(name)) !== -1) return containers[i];
    }
    return null;
  }

  function optionName(container) {
    var legend = container.querySelector('legend');
    if (legend && legend.firstChild) return legend.firstChild.textContent;
    var label = container.querySelector(':scope > label');
    if (label) return label.textContent;
    return '';
  }

  // All color values of this product, slugified, longest-first.
  function optionValues(container) {
    var values = [];
    var seen = {};
    function add(v) {
      var s = slugify(v);
      if (s && !seen[s]) {
        seen[s] = true;
        values.push(s);
      }
    }
    toArray(container.querySelectorAll('input[type="radio"]')).forEach(function (input) {
      add(input.value || input.getAttribute('value'));
    });
    toArray(container.querySelectorAll('select option')).forEach(function (option) {
      add(option.value || option.textContent);
    });
    values.sort(function (a, b) {
      return b.length - a.length;
    });
    return values;
  }

  function selectedColorSlug(picker) {
    var container = colorOptionContainer(picker);
    if (!container) return null;
    var checked = container.querySelector('input[type="radio"]:checked');
    if (checked) return slugify(checked.value || checked.getAttribute('value')) || null;
    var select = container.querySelector('select');
    if (select && select.value) return slugify(select.value) || null;
    var selOption = container.querySelector('select option[selected]');
    if (selOption) return slugify(selOption.value || selOption.textContent) || null;
    var swatchValue = container.querySelector('.variant-option__swatch-value');
    if (swatchValue && swatchValue.textContent.trim()) return slugify(swatchValue.textContent) || null;
    return null;
  }

  /* ========================= Gallery inventory ======================== */

  // All item lists are index-aligned with the product's media order:
  // index i in every list refers to the same product image.
  function galleryParts(gallery) {
    return {
      slides: toArray(gallery.querySelectorAll('slideshow-component slideshow-slide')),
      gridItems: toArray(gallery.querySelectorAll('ul.media-gallery__grid > li')),
      thumbs: toArray(gallery.querySelectorAll('slideshow-controls .slideshow-controls__thumbnail')),
      dots: toArray(gallery.querySelectorAll('slideshow-controls .slideshow-controls__dots > li')),
      zoomItems: toArray(gallery.querySelectorAll('.dialog-zoomed-gallery > li')),
      zoomThumbs: toArray(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')),
      counter: gallery.querySelector('.slideshow-controls__counter'),
      slideshow: gallery.querySelector('slideshow-component'),
    };
  }

  function firstImageSrc(el) {
    if (!el) return null;
    var img = el.querySelector('img');
    if (!img) return null;
    return img.getAttribute('src') || img.getAttribute('data-src') || img.currentSrc || null;
  }

  // color slug per media index (null = unknown, always kept visible)
  function indexColors(parts, knownSlugs) {
    var n = Math.max(parts.slides.length, parts.gridItems.length);
    var colors = [];
    for (var i = 0; i < n; i++) {
      var src = firstImageSrc(parts.slides[i]) || firstImageSrc(parts.gridItems[i]);
      colors.push(colorSlugFromUrl(src, knownSlugs));
    }
    return colors;
  }

  /* ============================ Filtering ============================= */

  function setVisible(el, visible) {
    if (!el) return;
    if (visible) {
      el.removeAttribute('hidden');
      el.style.display = '';
    } else {
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    }
  }

  function toggleIndex(parts, i, visible) {
    setVisible(parts.slides[i], visible);
    setVisible(parts.gridItems[i], visible);
    setVisible(parts.thumbs[i], visible);
    setVisible(parts.dots[i], visible);
    setVisible(parts.zoomItems[i], visible);
    setVisible(parts.zoomThumbs[i], visible);
  }

  function showAll(parts) {
    var all = parts.slides
      .concat(parts.gridItems, parts.thumbs, parts.dots, parts.zoomItems, parts.zoomThumbs);
    all.forEach(function (el) {
      setVisible(el, true);
    });
    updateCounter(parts.counter, null);
  }

  // The mobile counter shows "1 / N" — keep N in sync with visible slides.
  function updateCounter(counter, visibleCount) {
    if (!counter) return;
    var totalNode = null;
    for (var n = counter.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3 && n.textContent.trim() !== '') totalNode = n;
    }
    if (visibleCount == null) {
      if (counter.dataset.sgOrigTotal && totalNode) totalNode.textContent = counter.dataset.sgOrigTotal;
      return;
    }
    if (totalNode) {
      if (!counter.dataset.sgOrigTotal) counter.dataset.sgOrigTotal = totalNode.textContent;
      totalNode.textContent = String(visibleCount);
    }
  }

  function applyFilter() {
    var gallery = mainGallery();
    var picker = mainPicker();
    if (!gallery || !picker) {
      log('no product gallery or variant picker on this page');
      return;
    }

    var container = colorOptionContainer(picker);
    if (!container) {
      log('no color option found — showing all media');
      return;
    }

    var knownSlugs = optionValues(container);
    if (!knownSlugs.length) {
      log('no color values found — showing all media');
      return;
    }

    var parts = galleryParts(gallery);
    if (!parts.slides.length && !parts.gridItems.length) {
      log('no gallery media found');
      return;
    }

    var colors = indexColors(parts, knownSlugs);
    var matchedAny = colors.some(function (c) {
      return c !== null;
    });

    if (!matchedAny) {
      // Filenames carry no color info (e.g. joggers' {hash}_preview.png).
      log('no color info in image URLs — showing all media');
      showAll(parts);
      return;
    }

    var selected = selectedColorSlug(picker);
    if (!selected) {
      log('could not determine selected color — showing all media');
      showAll(parts);
      return;
    }

    var visibleCount = 0;
    for (var i = 0; i < colors.length; i++) {
      var visible = colors[i] === null || colors[i] === selected;
      if (visible) visibleCount++;
      toggleIndex(parts, i, visible);
    }
    updateCounter(parts.counter, visibleCount);
    log('filtered to color "' + selected + '" — ' + visibleCount + '/' + colors.length + ' visible');

    // Reset the (mobile) carousel to the first visible slide.
    var slideshow = parts.slideshow;
    if (slideshow && typeof slideshow.select === 'function') {
      try {
        slideshow.select(0, undefined, { animate: false });
      } catch (e) {
        log('slideshow reset failed (non-fatal)', e);
      }
    }
  }

  /* ============================== Wiring ============================== */

  function onPickerChange(event) {
    var picker = event && event.target && event.target.closest
      ? event.target.closest('variant-picker')
      : null;
    // Only react to the main product picker, ignore quick-add pickers.
    if (picker && picker !== mainPicker()) return;
    applyFilter();
  }

  function init() {
    if (!mainGallery() || !mainPicker()) return; // not a product page
    applyFilter(); // initial state (covers ?variant= deep links)

    // `change` bubbles from the picker's radios/selects — fires synchronously.
    document.addEventListener('change', onPickerChange);
    // `variant:update` fires after the theme re-renders the picker — safety net.
    document.addEventListener('variant:update', onPickerChange);

    // If the slideshow web component upgrades after us, re-apply so the
    // carousel reset uses the real component API.
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('slideshow-component').then(function () {
        applyFilter();
      });
    }

    log('initialized');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
